import { useMutation, useQueryClient } from '@tanstack/react-query';
import BadgeService, {
  ICreateBadgeParams,
} from '@clickersphere/core/services/BadgeService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BADGES_QUERY_KEY, BadgeQueryData } from './useBadges';

export function useCreateBadge() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: createBadgeIsPending, mutateAsync: createBadgeAsync } =
    useMutation({
      mutationFn: async (data: ICreateBadgeParams) => {
        const badge = await BadgeService.create(data);
        return badge as BadgeQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });

        const tempId = String(crypto.randomUUID());

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) => [
            ...(old ?? []),
            {
              id: tempId,
              tenantId: '',
              name: variables.name,
              color: variables.color,
              usageCount: 0,
              createdAt: Date.now(),
              queryStatus: 'pending',
            },
          ],
        );

        return { tempId };
      },

      onSuccess: async (data, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) =>
            old?.map((badge) => (badge.id === context.tempId ? data : badge)),
        );
      },

      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) => old?.filter((badge) => badge.id !== context?.tempId),
        );
      },
    });

  return {
    createBadgeAsync,
    createBadgeIsPending,
  };
}
